import { TABLE_EVENT } from 'constants/table';
import { ItemsPerPage } from 'constants/browse';
import { useCallback, useRef } from 'react';
import { useAuthentication } from 'contexts/AuthenticationProvider/AuthenticationProvider';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { useDebouncedState } from 'hooks/useDebouncedState/useDebouncedState';
import { tableSubscriptions } from 'helpers/tableEvent/tableEvent';
import { useLocation } from 'react-router-dom';
import { AudioConfigParams } from 'types/params';
import { useFreemiumGatingContext } from 'contexts/FreemiumGatingContext/FreemiumGatingContext';
import { usePrivateAction } from 'hooks/usePrivateAction/usePrivateAction';
import { useModalContext } from 'contexts/ModalContext/ModalContext';
import { useActivePlayerContext } from 'contexts/ActivePlayerContext/ActivePlayerContext';
import { useGoToPage } from './hooks';

type RowChangeEvent = {
    key?: 'ArrowUp' | 'ArrowDown';
    type?: 'click';
    target?: { type: string; ariaLabel?: string };
};

export const useSamplesTable = ({
    itemsPerPage,
    isFetching,
    onPageChanged,
}: {
    itemsPerPage: number;
    isFetching: boolean;
    onPageChanged: () => void;
}) => {
    const { isGuestUser, login } = useAuthentication();
    const { isAppGated } = useFreemiumGatingContext();
    const { goToPage } = useGoToPage();
    const doPrivateAction = usePrivateAction();
    const { openModal } = useModalContext();
    const { stopActivePlayer } = useActivePlayerContext();

    const { search } = useLocation();
    const urlSearchParams = new URLSearchParams(search);
    const autoPlay = urlSearchParams.get(AudioConfigParams.autoPlay);

    let initialFocusedRow = -1;

    if (autoPlay === 'first') {
        initialFocusedRow = 0;
    } else if (autoPlay === 'last') {
        initialFocusedRow = ItemsPerPage - 1;
    }

    const { page } = useCurrentSearchState();

    const focusedRowIndexRef = useRef(initialFocusedRow);

    // This callback is bound on all table events
    const handleRowChange = useCallback(
        (rowIndex: number, event: RowChangeEvent) => {
            if (event?.key === 'ArrowUp' || event?.key === 'ArrowDown') {
                // Previous Page
                if (rowIndex < 0 && page > 1) {
                    const nextPage = page - 1;
                    const autoPlay = 'last';

                    goToPage(nextPage, { autoPlay });

                    return;
                }

                // Next Page
                if (rowIndex >= itemsPerPage) {
                    const nextPage = page + 1;
                    const autoPlay = 'first';

                    onPageChanged();

                    goToPage(nextPage, { autoPlay });

                    return;
                }

                // Previous/Next Row
                if (0 <= rowIndex && rowIndex < itemsPerPage) {
                    tableSubscriptions[TABLE_EVENT.ROW_CHANGED].immediate({
                        rowIndex: rowIndex,
                        event: event,
                    });

                    return;
                }
            }

            const isButton = event?.target?.type === 'button';
            const isPlayerButton =
                event?.target?.ariaLabel === 'Play' ||
                event?.target?.ariaLabel === 'Pause' ||
                event?.target?.ariaLabel === 'PlayFilled' ||
                event?.target?.ariaLabel === 'PauseFilled';

            /**
             * Trigger this event when the user clicks anywhere on the row except
             *   on a button that is NOT the PlayerButton (ex: Add to project, Download, etc.)
             */
            if (event?.type === 'click' && (isPlayerButton || !isButton)) {
                tableSubscriptions[TABLE_EVENT.ROW_CLICKED].immediate({
                    rowIndex: rowIndex,
                    event: event,
                });
            }
        },
        [
            page,
            itemsPerPage,
            goToPage,
            onPageChanged,
            isGuestUser,
            login,
            stopActivePlayer,
            doPrivateAction,
            openModal,
            isAppGated,
        ],
    );

    const isFetchingDebounced = useDebouncedState(isFetching);

    return { handleRowChange, focusedRowIndexRef, isFetchingDebounced, page };
};
